"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Incoterms side-note pill — placed on contract / trade-pipeline screens so
 * the audience knows which side carries the insurance + transport cost.
 *
 *   CIF · seller arranges marine cargo insurance (T1, T2)
 *   FOB · buyer arranges insurance after loading              (T3)
 *
 * Compact by default; pass `verbose` for a one-line "who-arranges" hint.
 */
export function IncotermsPill({
  basis,
  who,
  verbose = false,
  className,
}: {
  basis: "CIF" | "FOB" | "CIP" | "EXW" | "DDP";
  who?: string;
  verbose?: boolean;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "state-traced-stripe inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider",
        "border-state-traced/40 bg-state-traced/10 text-state-traced",
        className,
      )}
    >
      <span className="font-semibold">Incoterms · {basis}</span>
      {verbose && who && (
        <span className="font-normal normal-case tracking-normal text-orbitra-paper">· {who}</span>
      )}
    </span>
  );
}

/**
 * "Insurance selected" mini-line — single-line attestation that an insurance
 * policy is in scope for this trade, with coverage, basis, and arranger.
 *
 * For CIF the seller arranges; the line reads e.g.
 *   Insurance · marine cargo · ≥ 110% CIF · Aurora-arranged
 * For FOB the buyer arranges; the line reads
 *   Insurance · marine cargo · ≥ 110% invoice · Pampagro-arranged (FOB)
 */
export function InsuranceSelectedLine({
  coverage = "≥ 110% CIF",
  policyType = "Marine cargo",
  arrangedBy,
  className,
}: {
  coverage?: string;
  policyType?: string;
  arrangedBy: string;
  className?: string;
}) {
  const t = useTranslations("logisticsInsurancePicker");
  return (
    <div
      className={clsx(
        "flex items-center gap-3 rounded-md border border-state-financed/30 bg-orbitra-ink/40 px-3 py-2 text-[11px]",
        className,
      )}
    >
      <span className="font-mono text-state-financed">●</span>
      <span className="text-orbitra-paper">
        Insurance · {policyType} · {coverage} · <span className="text-state-financed">{arrangedBy}</span>-arranged
      </span>
      <span className="ms-auto font-mono text-[10px] text-orbitra-muted">{t("selectedTag")}</span>
    </div>
  );
}
